import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { forwardRef, useRef, useState, useCallback, useImperativeHandle, useEffect } from "react";
import useMediaQuery from "@mui/system/useMediaQuery";
import Box from "@mui/material/Box";
import useTheme from "@mui/material/styles/useTheme";
import IconButton from "@mui/material/IconButton";
import MuiList from "@mui/material/List";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
function SpacedChildren({
  children,
  spacing
}) {
  if (!spacing) {
    return /* @__PURE__ */ jsx(Fragment, { children });
  }
  return /* @__PURE__ */ jsx(Box, { sx: {
    display: "flex",
    "& > *": {
      marginRight: spacing
    },
    flex: "none",
    scrollSnapAlign: "start"
  }, children });
}
function getArrowSx(direction) {
  return (theme) => ({
    position: "absolute",
    zIndex: 1,
    margin: 1,
    width: "40px",
    height: "40px",
    left: direction === "left" ? 0 : "auto",
    right: direction === "right" ? 0 : "auto",
    backgroundColor: theme.palette.neutral.background,
    borderRadius: "50%",
    border: `1px solid ${theme.palette.neutral.light}`,
    boxShadow: `0px 2px 5px 0px ${theme.palette.text.primary}33`,
    "&:focus": {
      outline: "none",
      border: `1px solid ${theme.palette.neutral.light}`,
      backgroundColor: theme.palette.neutral.light
    },
    "& .MuiSvgIcon-root": {
      fontSize: 14,
      color: `${theme.palette.text.primary}`
    },
    "&:active": {
      backgroundColor: theme.palette.neutral.light
    }
  });
}
const List = forwardRef(function List2({
  children,
  sideScroller,
  scrollAmountInPixels,
  showArrows,
  circularList,
  scrollCallback,
  spacing,
  sx,
  ...props
}, ref) {
  const scrollerRef = useRef(null);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const shouldShowArrows = showArrows !== void 0 ? showArrows : isDesktop;
  const [hovered, setHovered] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(!!circularList);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const handleMouseEnter = () => {
    setHovered(true);
  };
  const handleMouseLeave = () => {
    if (!isScrolling) {
      setHovered(false);
    }
  };
  const updateArrowVisibility = useCallback((scrollLeft, rightScrollLimit) => {
    setShowLeftArrow(scrollLeft > 0 || !!circularList);
    setShowRightArrow(scrollLeft < rightScrollLimit || !!circularList);
  }, [circularList]);
  const calculateScrollLeftValue = useCallback(({
    scrollLeft,
    scrollWidth,
    clientWidth
  }, steps) => {
    const scrollAmount = scrollAmountInPixels || clientWidth;
    const rightScrollLimit = scrollWidth - clientWidth;
    const left = scrollLeft + steps * scrollAmount;
    updateArrowVisibility(left, rightScrollLimit);
    if (left < 0) {
      return circularList && scrollLeft === 0 ? rightScrollLimit : 0;
    }
    if (left > rightScrollLimit) {
      return circularList && scrollLeft === rightScrollLimit ? 0 : rightScrollLimit;
    }
    return left;
  }, [circularList, scrollAmountInPixels, updateArrowVisibility]);
  const scroll = (steps) => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTo({
        left: calculateScrollLeftValue(scrollerRef.current, steps),
        behavior: "smooth"
      });
      setIsScrolling(true);
      setTimeout(() => {
        var _a;
        setIsScrolling(false);
        if (!((_a = scrollerRef.current) == null ? void 0 : _a.matches(":hover"))) {
          setHovered(false);
        }
      }, 200);
    }
  };
  useImperativeHandle(ref, () => ({
    scroll(steps) {
      scroll(steps);
    }
  }));
  useEffect(() => {
    if (scrollerRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollerRef.current;
      updateArrowVisibility(scrollLeft, scrollWidth - clientWidth);
    }
  }, [updateArrowVisibility]);
  useEffect(() => {
    if (scrollCallback) {
      const handleScroll = () => {
        scrollCallback(scrollerRef);
      };
      const container = scrollerRef.current;
      container == null ? void 0 : container.addEventListener("scroll", handleScroll);
      return () => {
        container == null ? void 0 : container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [scrollCallback]);
  const shouldShowArrowsAndHovered = showArrows !== void 0 ? showArrows : shouldShowArrows && hovered;
  if (!sideScroller) {
    return /* @__PURE__ */ jsx(MuiList, { sx, ...props, children });
  }
  return /* @__PURE__ */ jsxs(Box, { display: "flex", alignItems: "center", position: "relative", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [
    shouldShowArrowsAndHovered && showLeftArrow && /* @__PURE__ */ jsx(IconButton, { onClick: () => scroll(-1), disableRipple: true, sx: getArrowSx("left"), children: /* @__PURE__ */ jsx(ArrowBackIosNewIcon, {}) }),
    /* @__PURE__ */ jsx(MuiList, { ref: scrollerRef, sx: {
      display: "flex",
      overflow: "auto",
      scrollSnapType: "x mandatory",
      scrollBehavior: "smooth",
      "&::-webkit-scrollbar": {
        display: "none"
      },
      scrollbarWidth: "none",
      "-ms-overflow-style": "none",
      "& > *": {
        flex: "none",
        scrollSnapAlign: "start"
      },
      ...sx
    }, ...props, children: /* @__PURE__ */ jsx(SpacedChildren, { spacing, children }) }),
    shouldShowArrowsAndHovered && showRightArrow && /* @__PURE__ */ jsx(IconButton, { onClick: () => scroll(1), disableRipple: true, sx: getArrowSx("right"), children: /* @__PURE__ */ jsx(ArrowForwardIosIcon, {}) })
  ] });
});
export {
  List
};
//# sourceMappingURL=index.js.map
